/// <reference path="../../../../WebUI/typings/tsd.d.ts" />

import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Rest from "../../Generated/RestClient";
import { IAnnouncements } from "../../DevPortalAppBase";
import { DevPortalApp } from "../DevPortalApp";
import { NavLinks, SearchControl, JumboHeadControl, ProgressBarControl, resourceStrings } from "../../../../Intercom.ReactUI/Views/index";
import { Resources } from "../../Resources/Intercom.Resources.DevPortal";
import { clientAppSettings } from "../../Models/ClientAppSettings";
import { getSignInUrl } from "../../Utils/Links";
import isInFrame from "../../../../Intercom.ReactUI/Utils/InFrame";
import { parseUrl } from "../../../../Intercom.ReactUI/Utils/Url";
import { parseUserAgent, KnownBrowsers } from "../../../../Intercom.ReactUI/Utils/userAgent";
import { EmailSubscriptionConsentConductor } from '../../Controllers/EmailSubscriptionConsent';
import { isBrowserSupported } from "../Controllers/ControllerBase";
import { ControllerBase } from "../Controllers/ControllerBase";
import { MasterPageHeader } from "./MasterPageHeader";
import { combineClassNames } from "../../Utils/ReactUtils";
import { lazy } from "../../Decorators/index";
import { HeaderKind } from "../Utility/HeaderKind";
import { SupportedFeatureUtility } from "../Utility/SupportedFeatureUtility";

export interface SearchablePageProps {
    onSearch: (s: string) => void;
}

export interface MasterPageProps extends SearchablePageProps {
    controller: ControllerBase;
    signInRequired: boolean;
    headerKind: HeaderKind;
    isAdmin: () => boolean;
    isReviewer: () => boolean;
    onSignOutClicked: () => void;
    onUserAccountClicked: () => void;
    refreshSignedInUser: () => void;
    isRoutableUrl: (url: string) => boolean;
}

export interface MasterPageState {
    signInState: string;
    signedInUser: Rest.AuthData;
    signInInProgress: boolean;
    signInError: string;
    activeLink: NavLinks;
    searchString?: string;
    expandSearch?: boolean;
    unsupportedBrowser?: boolean;
    statusBar: JSX.Element;
    statusBarScrolls?: boolean;
    showProgressMask?: boolean;
    announcements?: IAnnouncements;
}

export class MasterPage extends React.Component<MasterPageProps, MasterPageState> {
    private singleSignOnChecker: HTMLIFrameElement;
    private emailSubscriptionConsent: EmailSubscriptionConsentConductor;
    public pageContent: HTMLDivElement;
    public pageHeader: MasterPageHeader;

    constructor(props) {
        super(props);
        this.state = {
            signInState: null,
            signedInUser: null,
            signInInProgress: false,
            signInError: null,
            activeLink: NavLinks.root,
            statusBar: null
        };

        this.emailSubscriptionConsent = new EmailSubscriptionConsentConductor();
    }

    render(): JSX.Element {
        if (this.state.unsupportedBrowser) {
            return this.renderUnsupportedBrowser();
        } else if (this.state.signInState) {
            return this.renderWaitListScreen();
        } else if (this.props.signInRequired && !this.state.signedInUser) {
            return this.renderSignInScreen();
        } else {
            return this.renderRegularScreen();
        }
    }

    public componentWillUpdate(nextProps: MasterPageProps, state: MasterPageState) {
        var jumbo = this.refs['jumbo'] as JumboHeadControl;
        if (jumbo) {
            jumbo.setState({ activeLink: state.activeLink });
        }

        var search = this.refs['searchBox'] as SearchControl;
        if (search) {
            search.setState({ searchString: state.searchString });
        }
    }

    public componentDidUpdate(prevProps: MasterPageProps, prevState: MasterPageState) {
        if (this.state.signedInUser) {
            this.emailSubscriptionConsent.showIfRequired();
        }
        if (this.pageContent) {
            this.props.controller.app.scrolled(this.pageContent);
        }
    }

    private toggleSearch() {
        var opened = _.isUndefined(this.state.expandSearch) ? true : !this.state.expandSearch;
        this.setState(_.extend(this.state, { expandSearch: opened }));
    }

    private userFlightedDocuemntationLink(): string {
        /*
        var features = clientAppSettings.flightedFeatures;
        if (_.indexOf(features, "v3") >= 0) {
            return '//aka.ms/bf-docs-nav';
        } else {
            return clientAppSettings.documentationUrl;
        }*/
        return '//docs.microsoft.com/en-us/bot-framework/';
    }

    private checkAzureSingleSignOn() {
        let host = null;
        try {
            host = this.singleSignOnChecker.contentWindow.location.hostname;
        } catch (error) {
            host = null;
        }
        if (host) {
            const curHost = window.location.hostname;
            if (host === curHost) {
                // user already logged in azure, fetched user and re-render
                this.props.refreshSignedInUser();
            }
        }
    }

    @lazy()
    private get userAgent(): { [browser: string]: boolean } {
        return parseUserAgent();
    }

    @lazy()
    private get isSafari(): boolean {
        const ua = this.userAgent as any;
        return ua.Safari && !(ua.Chrome || ua.Edge || ua.Firefox || ua.CriOS || ua.Opera);
    }

    private renderHiddenFrameToCheckAzureSingleSignOn(): JSX.Element {
        return null;
    }

    renderHeader(showLinks: boolean = true): JSX.Element {
        if (isInFrame() || this.props.headerKind === HeaderKind.None) {
            return null;
        }

        const showJumboHeader = this.props.headerKind === HeaderKind.Jumbo;
        const showUserMenu = SupportedFeatureUtility.isSupported(Rest.DevPortalFeatureFlags.SupportNonAzureUsers);

        return (
            <MasterPageHeader
                ref={x => this.pageHeader = x}
                controller={this.props.controller}
                showLinks={showLinks}
                showUserMenu={showUserMenu}
                showSearch={true}
                showEnvironment={true}
                showMainNavLink={true}
                isAdmin={this.props.isAdmin}
                isReviewer={this.props.isReviewer}
                signedInUser={() => this.state.signedInUser}
                renderHiddenFrameToCheckAzureSingleSignOn={() => this.renderHiddenFrameToCheckAzureSingleSignOn()}
                onSearch={this.props.onSearch}
                onSignOutClicked={this.props.onSignOutClicked}
                onAccountClicked={this.props.onUserAccountClicked}
                showJumboHeader={showJumboHeader}
                isRoutableUrl={this.props.isRoutableUrl}
                announcements={this.state.announcements}
            />
        );
    }

    renderRegularScreen(): JSX.Element {
        const scrollHandler = _.debounce((element) => this.props.controller.app.scrolled(element), 100, false);

        let outlineClassOverrides;
        switch (this.props.headerKind) {
            case HeaderKind.Small:
                outlineClassOverrides = "small";
                break;
            case HeaderKind.Jumbo:
                outlineClassOverrides = "";
                break;
            case HeaderKind.None:
                outlineClassOverrides = "no-header";
                break;
        }

        return (
            <div className="fullWidth">
                {this.renderHeader()}
                {this.state.statusBarScrolls ? null : this.state.statusBar}
                <section className="action-sheet">
                </section>

                <main className={combineClassNames("outline", outlineClassOverrides)} role="main">
                    <div
                        ref={x => this.pageContent = x}
                        id='page-content'
                        className={combineClassNames("page-content", outlineClassOverrides)}
                        onScroll={(event) => scrollHandler(event.nativeEvent.srcElement || event.nativeEvent.target)}
                    >
                        {this.state.statusBarScrolls ? this.state.statusBar : null}
                    </div>
                    <ProgressBarControl />
                </main>
                {
                    this.state.showProgressMask ? (<div className='dialog-mask'></div>) : null
                }
            </div>
        );
    }

    renderUnsupportedBrowser(): JSX.Element {
        return (
            <div className="sign-in-screen">
                {this.renderHeader()}
                <div className="sign-in-contents">
                    <div className="sign-in-title">{isInFrame() ? Resources.unsupportedBrowser_Azure : Resources.unsupportedBrowser}</div>
                </div>
            </div>);
    }

    renderWaitListScreen(): JSX.Element {
        return null;
    }

    renderSignInScreen(): JSX.Element {
        let bottomPanel: JSX.Element;
        let errorMessagePanel: JSX.Element;
        if (isInFrame()) {
            return null;
        }
        if (!SupportedFeatureUtility.isSupported(Rest.DevPortalFeatureFlags.SupportNonAzureUsers)) {
            return (
                <div className="sign-in-screen">
                    {this.renderHeader(false)}
                    <div className="sign-in-contents">
                        <div className="sign-in-title">Not supported</div>
                        <div className="sign-in-details">
                            <div>This feature is not supported in this environment.</div>
                            <div>Please manage your bots from the Azure portal.</div>
                        </div>
                    </div>
                </div>
            );
        }
        if (this.state.signInInProgress) {
            bottomPanel = (
                <div>
                    <div className="sign-in-progress-text">Signing in...</div>
                    <div className="sign-in-progress-animation"></div>
                </div>
            );
        }
        else {
            bottomPanel = (
                <div className="button-panel">
                    <a href={getSignInUrl()}><button type="button" className="sign-in default-button">Sign in</button></a>
                    {this.renderHiddenFrameToCheckAzureSingleSignOn()}
                </div>
            );
        }

        if (this.state.signInError) {
            errorMessagePanel = <div className="sign-in-error">{this.state.signInError}</div>;
        }

        return (
            <div className="sign-in-screen">
                {this.renderHeader(false)}
                <div className="sign-in-contents">
                    <div className="sign-in-title">Sign in</div>
                    <div className="sign-in-details">
                        <div>Welcome to {resourceStrings.botFramework} developer portal.</div>
                        <div>To continue sign in with your work or school, or personal Microsoft account.</div>
                    </div>
                    {bottomPanel}
                    {errorMessagePanel}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./Client/v4/Views/MasterPage.tsx